import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

const QUEUE_URLS = {
  hod: domainSettings.urls.academics.materialApprovalsHodQueue,
  section_head: domainSettings.urls.academics.materialApprovalsSectionHeadQueue,
  deputy_head: domainSettings.urls.academics.materialApprovalsDeputyHeadQueue,
  headteacher: domainSettings.urls.academics.materialApprovalsHeadteacherQueue,
};

export const useMaterialApprovals = () => {
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [queueMeta, setQueueMeta] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchQueue = useCallback(
    async (role, params = {}) => {
      setLoading(true);
      setQueueMeta(null);
      try {
        const baseUrl = QUEUE_URLS[role];
        if (!baseUrl) {
          message.error(`Unknown approval role: ${role}`);
          return;
        }
        const queryObj = {};
        if (params.acadyrID) queryObj.acadyrID = params.acadyrID;
        if (params.termID) queryObj.termID = params.termID;
        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${baseUrl}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          const payload = response.data;
          setQueue(Array.isArray(payload) ? payload : payload?.results || []);
          setQueueMeta(Array.isArray(payload) ? null : payload?.meta || null);
        } else {
          Utilities.showError(response, "Failed to fetch approval queue");
        }
      } catch (error) {
        message.error("Error fetching approval queue");
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const createApproval = useCallback(
    async (payload) => {
      setLoading(true);
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.academics.materialApprovals,
          payload,
          navigate,
        );
        if (response.ok) {
          message.success("Action recorded successfully");
          return true;
        }
        Utilities.showError(response, "Failed to record action");
        return false;
      } catch (error) {
        message.error("Error recording action");
        console.error(error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const unlockMaterial = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const url =
          domainSettings.urls.academics.instructionalMaterialUnlock.replace(
            ":id",
            id,
          );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          message.success("Material unlocked successfully");
          return true;
        }
        Utilities.showError(response, "Failed to unlock material");
        return false;
      } catch (error) {
        message.error("Error unlocking material");
        console.error(error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  return {
    queue,
    queueMeta,
    loading,
    fetchQueue,
    createApproval,
    unlockMaterial,
  };
};

export default useMaterialApprovals;
